
import { useState, useEffect } from 'react';
import DashboardLayout from './components/DashboardLayout';
import StatsCard from './components/StatsCard';
import { supabase } from '../../lib/supabase';

export default function Dashboard() {
  const [userRole] = useState('manager'); // TODO: Get from auth context
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    todayOrders: 0,
    totalProducts: 0,
    totalStock: 0,
    activePartners: 0
  });

  const loadStats = async () => {
    try {
      // Lấy tổng doanh thu từ đơn hàng đã giao
      const { data: revenueData } = await supabase
        .from('orders')
        .select('total_amount')
        .eq('status', 'delivered');
      
      const totalRevenue = revenueData?.reduce((sum, order) => sum + order.total_amount, 0) || 0;

      // Đếm đơn hàng hôm nay
      const today = new Date().toISOString().split('T')[0];
      const { count: todayOrdersCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', `${today}T00:00:00.000Z`)
        .lt('created_at', `${today}T23:59:59.999Z`);

      // Đếm tổng số sản phẩm
      const { count: totalProducts } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Tính tổng tồn kho từ product_variants
      const { data: stockData } = await supabase
        .from('product_variants')
        .select('stock_quantity');
      
      const totalStock = stockData?.reduce((sum, variant) => sum + (variant.stock_quantity || 0), 0) || 0;

      // Đếm số đối tác hoạt động
      const { count: partnersCount } = await supabase
        .from('partners')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      setStats({
        totalRevenue,
        todayOrders: todayOrdersCount || 0,
        totalProducts: totalProducts || 0,
        totalStock,
        activePartners: partnersCount || 0
      });

    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // Keep stats at 0 if error
      setStats({
        totalRevenue: 0,
        todayOrders: 0,
        totalProducts: 0,
        totalStock: 0,
        activePartners: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const statsCards = [
    {
      title: 'Tổng doanh thu',
      value: loading ? '...' : `${stats.totalRevenue.toLocaleString('vi-VN')} ₫`,
      change: stats.totalRevenue > 0 ? '+0%' : '0%',
      changeType: 'neutral' as const,
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: 'Đơn hàng hôm nay',
      value: loading ? '...' : stats.todayOrders.toString(),
      change: stats.todayOrders > 0 ? '+0%' : '0%',
      changeType: 'neutral' as const,
      icon: 'ri-shopping-cart-line'
    },
    {
      title: 'Sản phẩm tồn kho',
      value: loading ? '...' : `${stats.totalStock.toLocaleString('vi-VN')} sản phẩm`,
      change: stats.totalStock > 0 ? '+0%' : '0%',
      changeType: 'neutral' as const,
      icon: 'ri-box-3-line'
    },
    {
      title: 'Đối tác hoạt động',
      value: loading ? '...' : stats.activePartners.toString(),
      change: stats.activePartners > 0 ? '+0%' : '0%',
      changeType: 'neutral' as const,
      icon: 'ri-team-line'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-black">Dashboard</h1>
            <p className="text-gray-600 mt-2">Tổng quan hệ thống quản lý bán hàng Navik</p>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-500 bg-white px-6 py-3 rounded-xl border border-gray-200 shadow-sm">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-time-line text-orange-500"></i>
            </div>
            <span>Cập nhật: {new Date().toLocaleString('vi-VN')}</span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Welcome Message */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-hand-heart-line text-2xl text-orange-600"></i>
              </div>
              <h2 className="text-xl font-semibold text-black">Chào mừng đến với Navik</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Hệ thống quản lý bán hàng của bạn đã sẵn sàng. Bắt đầu bằng cách thêm sản phẩm và tạo đơn hàng đầu tiên.
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-black mb-2">Bước tiếp theo:</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-checkbox-circle-line text-orange-500"></i>
                    </div>
                    Thêm sản phẩm vào kho
                  </li>
                  <li className="flex items-center">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-checkbox-circle-line text-orange-500"></i>
                    </div>
                    Mời đối tác tham gia
                  </li>
                  <li className="flex items-center">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-checkbox-circle-line text-orange-500"></i>
                    </div>
                    Tạo đơn hàng đầu tiên
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Inventory Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-archive-line text-2xl text-blue-600"></i>
              </div>
              <h2 className="text-xl font-semibold text-black">Tình trạng kho hàng</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <span className="font-medium text-black">Tổng sản phẩm</span>
                </div>
                <span className="text-sm text-blue-600 font-medium">{stats.totalProducts} loại</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="font-medium text-black">Tổng tồn kho</span>
                </div>
                <span className="text-sm text-green-600 font-medium">{stats.totalStock.toLocaleString('vi-VN')} sản phẩm</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                  <span className="font-medium text-black">Tự động trừ kho</span>
                </div>
                <span className="text-sm text-orange-600 font-medium">Đã kích hoạt</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-black mb-6">Thao tác nhanh</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <button className="flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 group border border-gray-200 hover:border-orange-200">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-add-circle-line text-2xl text-orange-600"></i>
              </div>
              <span className="text-sm font-medium text-black">Thêm sản phẩm</span>
            </button>
            
            <button className="flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 group border border-gray-200 hover:border-orange-200">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-shopping-cart-2-line text-2xl text-orange-600"></i>
              </div>
              <span className="text-sm font-medium text-black">Tạo đơn hàng</span>
            </button>
            
            <button className="flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 group border border-gray-200 hover:border-orange-200">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-user-add-line text-2xl text-orange-600"></i>
              </div>
              <span className="text-sm font-medium text-black">Thêm đối tác</span>
            </button>
            
            <button className="flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 group border border-gray-200 hover:border-orange-200">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-file-chart-line text-2xl text-orange-600"></i>
              </div>
              <span className="text-sm font-medium text-black">Xem báo cáo</span>
            </button>
          </div>
        </div>

        {/* Getting Started Guide */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200 p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mr-4">
              <i className="ri-lightbulb-line text-2xl text-white"></i>
            </div>
            <h2 className="text-xl font-semibold text-black">Hướng dẫn bắt đầu</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 border border-orange-200">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-box-3-line text-xl text-orange-600"></i>
              </div>
              <h3 className="font-semibold text-black mb-2">1. Quản lý sản phẩm</h3>
              <p className="text-sm text-gray-600 mb-4">Thêm sản phẩm, quản lý kho và theo dõi tồn kho.</p>
              <button className="text-orange-600 hover:text-orange-700 text-sm font-medium hover:underline transition-all duration-200 whitespace-nowrap">
                Bắt đầu →
              </button>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-orange-200">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-team-line text-xl text-orange-600"></i>
              </div>
              <h3 className="font-semibold text-black mb-2">2. Mời đối tác</h3>
              <p className="text-sm text-gray-600 mb-4">Thêm đối tác bán hàng và quản lý hoa hồng.</p>
              <button className="text-orange-600 hover:text-orange-700 text-sm font-medium hover:underline transition-all duration-200 whitespace-nowrap">
                Mời ngay →
              </button>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-orange-200">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-line text-xl text-orange-600"></i>
              </div>
              <h3 className="font-semibold text-black mb-2">3. Tạo đơn hàng</h3>
              <p className="text-sm text-gray-600 mb-4">Bắt đầu bán hàng và theo dõi đơn hàng.</p>
              <button className="text-orange-600 hover:text-orange-700 text-sm font-medium hover:underline transition-all duration-200 whitespace-nowrap">
                Tạo đơn →
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
